import React, { useState } from 'react';
import { vendorIntegrationsData, vendorCategoriesData } from '../../data/marketplaceData';
import { ArrowRight, Check, ExternalLink, Search, Filter } from 'lucide-react';
import { Button } from '../ui/button';

const VendorIntegrations = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredVendors = vendorIntegrationsData.filter(vendor => {
    const matchesCategory = selectedCategory === 'all' || vendor.category === selectedCategory;
    const matchesSearch = vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          vendor.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const uniqueCategories = [...new Set(vendorIntegrationsData.map(v => v.category))];

  return (
    <section className="py-24 bg-[#e3e4e6]/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-[#1297fd]/10 text-[#1297fd] rounded-full text-sm font-medium mb-4">
            Integraciones
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Conéctate con{' '}
            <span className="text-[#1297fd]">vendors líderes</span>
          </h2>
          <p className="text-lg text-[#c3c3c5] max-w-3xl mx-auto">
            Integra instantáneamente con los principales proveedores y gestiona soluciones basadas en suscripción de manera más eficiente.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-12">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#c3c3c5]" />
            <input
              type="text"
              placeholder="Buscar vendor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-[#e3e4e6] bg-white focus:outline-none focus:border-[#1297fd] focus:ring-2 focus:ring-[#1297fd]/20 transition-all"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-[#1297fd] text-white'
                  : 'bg-white text-gray-600 border border-[#e3e4e6] hover:border-[#1297fd]'
              }`}
            >
              Todos
            </button>
            {uniqueCategories.slice(0, 4).map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-[#1297fd] text-white'
                    : 'bg-white text-gray-600 border border-[#e3e4e6] hover:border-[#1297fd]'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Vendors Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVendors.map((vendor) => (
            <div
              key={vendor.id}
              className="group bg-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all duration-300 hover:shadow-xl hover:shadow-[#1297fd]/10 hover:-translate-y-1"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center p-2 group-hover:scale-110 transition-transform">
                    <span className="text-xl font-bold text-[#1297fd]">{vendor.name.charAt(0)}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 group-hover:text-[#1297fd] transition-colors">
                      {vendor.name}
                    </h3>
                    <span className="text-xs text-[#8bcaf9] font-medium">{vendor.category}</span>
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-[#c3c3c5] group-hover:text-[#1297fd] transition-colors" />
              </div>

              {/* Description */}
              <p className="text-[#c3c3c5] text-sm mb-4 leading-relaxed">
                {vendor.description}
              </p>

              {/* Features */}
              <div className="flex flex-wrap gap-2 mb-4">
                {vendor.features.slice(0, 3).map((feature) => (
                  <span
                    key={feature}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-[#e3e4e6]/50 rounded-lg text-xs text-gray-600"
                  >
                    <Check className="w-3 h-3 text-[#1297fd]" />
                    {feature}
                  </span>
                ))}
                {vendor.features.length > 3 && (
                  <span className="px-2 py-1 bg-[#8bcaf9]/20 rounded-lg text-xs text-[#1297fd] font-medium">
                    +{vendor.features.length - 3} más
                  </span>
                )}
              </div>

              {/* CTA */}
              <a
                href="#"
                className="inline-flex items-center text-[#1297fd] font-medium text-sm hover:text-[#40aaf8] group/link"
              >
                Ver integración
                <ArrowRight className="ml-1 w-4 h-4 transition-transform group-hover/link:translate-x-1" />
              </a>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="mt-16 rounded-3xl p-8 sm:p-12 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 50% 50%, white 1px, transparent 1px)`,
              backgroundSize: '30px 30px'
            }} />
          </div>

          <div className="relative flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                ¿Necesitas una integración personalizada?
              </h3>
              <p className="text-white/80">
                Nuestro equipo puede ayudarte a conectar con cualquier vendor mediante nuestras APIs abiertas.
              </p>
            </div>
            <Button
              size="lg"
              className="bg-white hover:bg-[#e3e4e6] text-[#1297fd] font-semibold px-8 py-6 rounded-full transition-all hover:scale-105 hover:shadow-xl whitespace-nowrap"
            >
              Contactar equipo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VendorIntegrations;
